import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Droplet, Sun, FileText, Video, Home, Palette, Box, User, Package, Search, ShoppingBag, Phone } from 'lucide-react';
import api from './services/cloudflare-api';

// Icon mapping
const iconMap = {
  Home,
  Box,
  Palette,
  User,
  Package,
  Sun,
  Droplet,
  FileText,
  Video
};

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('real-estate');
  const [activeService, setActiveService] = useState('image-enhancement');
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [mobileActiveCategory, setMobileActiveCategory] = useState(null);
  const [mobileActiveService, setMobileActiveService] = useState(null);

  // Dynamic service data from D1 API
  const [servicesData, setServicesData] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [categoryServicesMap, setCategoryServicesMap] = useState({});

  // Timeout ref for delayed menu closing (100ms delay)
  const servicesTimeoutRef = useRef(null);

  // Load services data from D1 API
  useEffect(() => {
    const loadServicesData = async () => {
      try {
        // console.log('📡 Navigation: Loading services from D1 API...');
        const data = await api.getServicesTree();
        setServicesData(data);
        processServicesData(data);
        // console.log('✅ Navigation: Loaded services from D1 API');
      } catch (error) {
        console.error('❌ Navigation: Failed to load services from D1 API:', error);
        // Fallback to empty structure
        setServicesData({ lineups: [] });
        processServicesData({ lineups: [] });
      }
    };

    loadServicesData();

    // Listen for servicesUpdated event from ServiceManager
    const handleServicesUpdate = () => {
      // console.log('📢 Navigation: Received servicesUpdated event, reloading...');
      loadServicesData();
    };

    window.addEventListener('servicesUpdated', handleServicesUpdate);
    return () => window.removeEventListener('servicesUpdated', handleServicesUpdate);
  }, []);

  // Process services data into navigation format
  const processServicesData = (data) => {
    if (!data || !data.lineups) return;

    // Transform lineups into service categories - filter out hidden lineups
    const categories = data.lineups
      .filter(lineup => !lineup.hidden)
      .map(lineup => ({
        id: lineup.id,
        name: lineup.name,
        icon: iconMap[lineup.icon] || Package,
        href: `#${lineup.id}`
      }));

    setServiceCategories(categories);

    // Set first category as active if not set
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }

    // Create map of categories to their services - filter out hidden items
    const servicesMap = {};
    data.lineups
      .filter(lineup => !lineup.hidden)
      .forEach(lineup => {
        servicesMap[lineup.id] = lineup.categories
          .filter(category => !category.hidden)
          .map(category => ({
            id: category.id,
            title: category.name,
            icon: iconMap[category.icon] || Sun,
            href: `#${category.id}`,
            items: category.services
              .filter(service => !service.hidden)
              .map(service => ({
                name: service.name,
                // Use service route if available, otherwise generate from name
                href: service.route || `/${service.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
              }))
          }));
      });

    setCategoryServicesMap(servicesMap);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav id="Menu" className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-screen-xl mx-auto px-4">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <Link to="/home" className="flex items-center space-x-2">
            <div className="relative group" style={{ perspective: '1000px' }}>
              <img
                src="/medios_logo.jpeg"
                alt="Medios"
                className="w-8 h-8 rounded-lg object-cover transition-all duration-300 hover:scale-105"
                style={{
                  transform: 'rotateX(5deg) rotateY(-5deg)',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.1), 0 20px 40px rgba(0,0,0,0.08), 5px 5px 0px rgba(57,81,115,0.1)'
                }}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold tracking-tight leading-tight drop-shadow-[0_2px_3px_rgba(0,0,0,0.15)]">Medios Solutions</span>
              <span className="text-[10px] text-gray-600 tracking-wide">Everything is about details</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 text-sm">
            {/* Services Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => {
                if (servicesTimeoutRef.current) {
                  clearTimeout(servicesTimeoutRef.current);
                }
                setIsServicesOpen(true);
              }}
              onMouseLeave={() => {
                servicesTimeoutRef.current = setTimeout(() => setIsServicesOpen(false), 100);
              }}
            >
              <button className="text-gray-900 hover:text-gray-600 transition-colors">
                Services
              </button>

              {/* Mega Menu Dropdown */}
              {isServicesOpen && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-[90vw] max-w-6xl bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-100 pt-6 pb-6 mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex gap-8 px-8">
                    {/* Left Sidebar - Categories */}
                    <div className="w-56 flex-shrink-0 border-r border-gray-200 pr-6">
                      {serviceCategories.map((category) => {
                        const Icon = category.icon;
                        return (
                          <button
                            key={category.id}
                            onMouseEnter={() => setActiveCategory(category.id)}
                            className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 mb-2 ${
                              activeCategory === category.id
                                ? 'bg-blue-50 text-blue-600'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <Icon size={20} />
                            <span className="font-medium text-sm">{category.name}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Middle Column - Service List */}
                    <div className="w-64 flex-shrink-0 border-r border-gray-200 pr-6">
                      {categoryServicesMap[activeCategory] && categoryServicesMap[activeCategory].length > 0 ? (
                        <>
                          {categoryServicesMap[activeCategory].map((service) => {
                            const Icon = service.icon;
                            return (
                              <button
                                key={service.id}
                                onMouseEnter={() => setActiveService(service.id)}
                                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 mb-2 ${
                                  activeService === service.id
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <Icon size={18} />
                                <span className="font-medium text-sm">{service.title}</span>
                              </button>
                            );
                          })}
                        </>
                      ) : (
                        <div className="text-sm text-gray-400 px-4 py-3">
                          No categories available
                        </div>
                      )}
                    </div>

                    {/* Right Column - Service Details */}
                    <div className="flex-1">
                      {categoryServicesMap[activeCategory] && activeService && (
                        <div className="grid grid-cols-2 gap-4">
                          {categoryServicesMap[activeCategory]
                            .find((s) => s.id === activeService)
                            ?.items.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className="px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-primary font-medium"
                              >
                                {item.name}
                              </a>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" className="text-gray-900 hover:text-gray-600 transition-colors">
              Blog
            </Link>
            <Link to="/about" className="text-gray-900 hover:text-gray-600 transition-colors">
              About
            </Link>
            <Link to="/support" className="text-gray-900 hover:text-gray-600 transition-colors">
              Support
            </Link>
            <Link to="/contact" className="text-gray-900 hover:text-gray-600 transition-colors">
              Contact
            </Link>
          </div>

          {/* Right Icons */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+1-917-720-5208" className="flex items-center gap-2 text-gray-900 hover:text-gray-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+1-917-720-5208</span>
            </a>
            <a href="https://www.mediossolutions.com/signin" className="bg-primary text-white px-5 py-2 rounded-full hover:bg-primary-dark transition-all hover:scale-105 text-sm inline-block">
              Login
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              {/* Mobile Services Dropdown */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="w-full text-left text-sm hover:text-blue-600 transition-colors py-2 flex items-center justify-between"
                >
                  Services
                  <ChevronDown size={16} className={`transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-4 mt-2 space-y-2">
                    {/* Service Categories */}
                    {serviceCategories.map((category) => (
                      <div key={category.id}>
                        <button
                          onClick={() => setMobileActiveCategory(mobileActiveCategory === category.id ? null : category.id)}
                          className="w-full text-left text-sm text-gray-700 hover:text-primary transition-colors py-2 flex items-center justify-between"
                        >
                          {category.name}
                          <ChevronDown size={14} className={`transition-transform ${mobileActiveCategory === category.id ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Show services for selected category */}
                        {mobileActiveCategory === category.id && categoryServicesMap[category.id] && (
                          <div className="pl-4 mt-1 space-y-1">
                            {categoryServicesMap[category.id].map((service) => (
                              <div key={service.id}>
                                <button
                                  onClick={() => setMobileActiveService(mobileActiveService === service.id ? null : service.id)}
                                  className="w-full text-left text-xs text-gray-700 hover:text-primary transition-colors py-1.5 flex items-center justify-between"
                                >
                                  {service.title}
                                  <ChevronDown size={12} className={`transition-transform ${mobileActiveService === service.id ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Show service items */}
                                {mobileActiveService === service.id && (
                                  <div className="pl-3 mt-1 space-y-1">
                                    {service.items.map((item) => (
                                      <a
                                        key={item.name}
                                        href={item.href}
                                        className="block text-xs text-gray-500 hover:text-blue-600 transition-colors py-1"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                      >
                                        • {item.name}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Show "No categories" if empty */}
                        {mobileActiveCategory === category.id && (!categoryServicesMap[category.id] || categoryServicesMap[category.id].length === 0) && (
                          <div className="pl-4 mt-1">
                            <p className="text-xs text-gray-400 py-1">No categories available</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/blog"
                className="text-sm hover:text-blue-600 transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/support"
                className="text-sm hover:text-blue-600 transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Support
              </Link>
              <Link
                to="/contact"
                className="text-sm hover:text-blue-600 transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>
              <a
                href="tel:+1-917-720-5208"
                className="flex items-center gap-2 text-sm text-gray-900 hover:text-gray-600 transition-colors py-2"
              >
                <Phone className="w-4 h-4" />
                <span>+1-917-720-5208</span>
              </a>
              <a
                href="https://www.mediossolutions.com/signin"
                className="bg-primary text-white px-5 py-3 rounded-full hover:bg-primary-dark transition-all text-sm text-center block"
              >
                Login
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
